import {
  Box,
  Typography,
  Slider,
  Stack,
  Button,
  Paper,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";

interface PayIn4CalculatorProps {
  amount: number;
  onProceed: () => void;
  setAmount: (amount: number) => void;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 2,
  }).format(n);

export default function PayIn4Calculator({
  amount,
  onProceed,
  setAmount,
}: PayIn4CalculatorProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (_: Event, newValue: number | number[]) => {
    setAmount(Array.isArray(newValue) ? newValue[0] : newValue);
  };

  const installment = amount / 4;
  const getDate = (i: number) =>
    dayjs()
      .add(i * 2, "week")
      .format("MMM D, YYYY");

  const marks = isMobile
    ? undefined
    : [
        { value: 200, label: "200" },
        { value: 1000, label: "1000" },
        { value: 2000, label: "2000" },
      ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 8px 28px rgba(0,0,0,.06)",
        width: "100%",
        maxWidth: 560,
        mx: "auto",
        overflow: "hidden",
      }}
    >
      <Stack spacing={2}>
        <Chip
          label="Pay in 4"
          color="secondary"
          variant="filled"
          sx={{ alignSelf: "flex-start", fontWeight: 700 }}
        />

        <Typography variant="h6" fontWeight={800} color="text.primary">
          Split your bond into 4 easy payments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No interest. No late fees. Just a simple plan that works.
        </Typography>

        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.default",
            textAlign: "center",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            You're applying for
          </Typography>
          <Typography
            variant="h4"
            fontWeight={900}
            color="primary"
            sx={{ lineHeight: 1 }}
          >
            {formatCurrency(amount)} Bond
          </Typography>
        </Box>

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" fontWeight={700} color="text.primary">
              Adjust the amount
            </Typography>
            {!isMobile && (
              <Typography variant="caption" color="text.secondary">
                Min 200 • Max 2000
              </Typography>
            )}
          </Stack>

          <Slider
            value={amount}
            onChange={handleChange}
            min={200}
            max={2000}
            step={25}
            marks={marks}
            valueLabelDisplay={isMobile ? "off" : "on"}
            aria-label="Bond amount"
            getAriaValueText={(v) => `${v} dollars`}
            valueLabelFormat={(v) => formatCurrency(Number(v))}
            sx={{
              height: 8,
              color: theme.palette.secondary.main,
              "& .MuiSlider-thumb": {
                height: isMobile ? 20 : 24,
                width: isMobile ? 20 : 24,
                backgroundColor: theme.palette.background.paper,
                border: `2px solid ${theme.palette.secondary.main}`,
                boxShadow: "0 2px 8px rgba(0,0,0,.12)",
              },
              "& .MuiSlider-track": { border: "none" },
              "& .MuiSlider-rail": { opacity: 0.3 },
              "& .MuiSlider-valueLabel": {
                borderRadius: 1,
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
              },
            }}
          />
          {isMobile && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Min 200 • Max 2000
            </Typography>
          )}
        </Box>

        <Divider />

        <Stack spacing={1}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: { xs: 1, md: 1.5 },
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.default",
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: i === 0 ? "primary.main" : "divider",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color={i === 0 ? "primary.main" : "text.primary"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {i === 0 ? "Today" : getDate(i)}
                </Typography>
              </Stack>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color="secondary.main"
                sx={{ ml: 1 }}
              >
                {formatCurrency(installment)}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 0.5,
            p: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.default",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={700}>Total</Typography>
          <Typography fontWeight={900} color="secondary.main">
            {formatCurrency(amount)}
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="secondary"
          onClick={onProceed}
          fullWidth
          sx={{ fontWeight: 700, py: { xs: 1, md: 1.25 } }}
        >
          Continue — {formatCurrency(installment)} × 4
        </Button>

        <Typography
          variant="caption"
          sx={{ textAlign: "center", color: "text.secondary" }}
        >
          No extra fees. Instant approval decision.
        </Typography>
      </Stack>
    </Paper>
  );
}
